import { displayUnitOf } from "@heraion/shared";
import { useMemo } from "react";
import type { EffortScale } from "@heraion/shared";
import type { LoggerHostDeps } from "@heraion/ui";

import { useProfileDoc } from "../useProfileDoc";

// The web's LoggerProfile, in one place.
//
// `LoggerProfile` is `Omit<Profile, 'id'|'createdAt'|'updatedAt'>`, and
// `useProfileDoc`'s doc is an untyped bag that starts `null` before the load
// (web has no ProfileSnapshot projection like mobile's always-defaulted
// useProfile()). `useLoggerScreen` reads `profile.unitsPreference` and
// `profile.effortScale` with NO `??` of its own, so those two need a real
// default here — the same house defaults CatalogScreen/WorkoutsScreen (kg)
// and EffortScreen (rir) already fall back to. Everything else the Logger
// reads is optional-chained downstream.
//
// ⭐ PE-39/K5 — lifted out of useLoggerHostDeps because it has a SECOND caller
// now. The prescribe editor is the only program editor there is, and the web
// mounts it from PlanScreen, which needs the same profile but none of the rest
// of the host-deps bag (programs, maxes, targets, the offline-queue stubs):
// calling the whole hook there would have fired three network reads to obtain
// one object [[feedback_reuse_nearest_sibling]].
export function useLoggerProfile(): LoggerHostDeps["profile"] {
  const { doc } = useProfileDoc();
  return useMemo(
    () =>
      ({
        ...(doc ?? {}),
        name: (doc?.["name"] as string | undefined) ?? "",
        unitsPreference: displayUnitOf(doc),
        effortScale: (doc?.["effortScale"] as EffortScale | undefined) ?? "rir",
      }) as LoggerHostDeps["profile"],
    [doc],
  );
}
